import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  createAllocation as createAllocationRequest,
  deleteAllocation as deleteAllocationRequest,
  getAllocationEntries,
  getAllocations,
  getDictionaryItems,
  getJournalEntry,
  previewAllocation,
  rerunAllocation,
} from "../../../../api/financeApi";
import { mapJournalEntry } from "../OperationsJournal/mapJournalEntry";
import Stepper from "./components/Stepper/Stepper";
import OperationsStep from "./components/OperationsStep/OperationsStep";
import RecipientsStep from "./components/RecipientsStep/RecipientsStep";
import BaseStep from "./components/BaseStep/BaseStep";
import ReviewStep from "./components/ReviewStep/ReviewStep";
import ResultStep from "./components/ResultStep/ResultStep";
import HistoryTab from "./components/HistoryTab/HistoryTab";
import Toast from "../shared/recognition/ui/Toast";
import ConfirmDialog from "../shared/recognition/ui/ConfirmDialog";
import { formatAmount } from "../OperationsJournal/mock/operationsJournalMock";
import { EMPTY_BASE, calculateAllocation, parsePercent } from "./utils/allocationBase";
import { buildAllocationRecord } from "./utils/allocationRecord";
import { ALLOCATION_STEPS, ALLOCATION_TABS, ONLY_THIS_RECORD_NOTE } from "./constants";
import "./Allocation.css";

const mapAllocationRun = async (run) => {
  let entries = [];
  try {
    const page = await getAllocationEntries(run.id, { limit: 200 });
    entries = (page?.items || []).map((item) => {
      const row = mapJournalEntry(item, "management");
      const projectName = row.projectDt || row.projectKt || row.organization || "—";
      return {
        id: row.id,
        document: row.document,
        sourceDocument: row.document,
        sourceDate: row.date,
        project: { id: projectName, code: "", name: projectName },
        share: 0,
        amount: row.amount || 0,
      };
    });
  } catch {
    entries = [];
  }

  const params = run.params || {};
  const recipients = params.recipients || [];
  const total = Number(run.total_amount) || 0;
  const started = run.run_at ? new Date(run.run_at) : null;
  const baseType = params.base?.type || run.base_type || "equal";
  return {
    id: run.id,
    document: run.code,
    date: started ? format(started, "dd.MM.yyyy") : "",
    launchedAt: started ? format(started, "dd.MM.yyyy HH:mm") : "",
    author: run.user_name || "—",
    period: [run.period_from, run.period_to].filter(Boolean).join(" – "),
    baseShort: run.base_type,
    baseLabel: run.base_type,
    operationsCount: params.source?.entry_ids?.length || 0,
    recipientsCount: recipients.length,
    total,
    restarted: false,
    rows: recipients.map((name) => ({
      project: { id: name, code: name, name },
      share: recipients.length ? 1 / recipients.length : 0,
      amount: recipients.length ? total / recipients.length : 0,
    })),
    entries,
    params: {
      operations: [],
      entryIds: params.source?.entry_ids || [],
      projectIds: recipients,
      base: {
        ...EMPTY_BASE,
        method: baseType === "custom" ? "manual" : baseType,
        articles: {
          ...EMPTY_BASE.articles,
          [baseType]: params.base?.items || [],
        },
        manualShares: params.base?.percents || {},
      },
    },
  };
};

const RESULT_STEP = ALLOCATION_STEPS.length - 1;
const REVIEW_STEP = RESULT_STEP - 1;

function Allocation() {
  const [activeTab, setActiveTab] = useState("new");
  const [step, setStep] = useState(0);
  const [wizardKey, setWizardKey] = useState(0);
  const [selectedOperations, setSelectedOperations] = useState(() => new Map());
  const [selectedProjects, setSelectedProjects] = useState(() => new Set());
  const [base, setBase] = useState(EMPTY_BASE);
  const [history, setHistory] = useState([]);
  const [recipientCatalog, setRecipientCatalog] = useState([]);
  const [notice, setNotice] = useState(null);
  const [createdRecord, setCreatedRecord] = useState(null);
  const [restartRecord, setRestartRecord] = useState(null);
  const [pendingRestart, setPendingRestart] = useState(null);
  const cancelPendingRestart = useCallback(() => setPendingRestart(null), []);
  const [toast, setToast] = useState(null);
  const closeToast = useCallback(() => setToast(null), []);
  const { userData } = useContext(UserContext);
  const authorName = userData
    ? `${userData.surname || ""} ${(userData.name || "").charAt(0)}.`.trim()
    : "Пользователь";

  const operations = useMemo(() => [...selectedOperations.values()], [selectedOperations]);
  const operationsTotal = operations.reduce((sum, row) => sum + (row.amount || 0), 0);
  const recipientProjects = useMemo(
    () => recipientCatalog.filter((project) => selectedProjects.has(project.id)),
    [recipientCatalog, selectedProjects]
  );
  const allocation = useMemo(() => {
    const local = calculateAllocation(recipientProjects, operationsTotal, base);
    if (!local.error || base.method === "equal" || base.method === "manual") return local;
    const share = recipientProjects.length ? 1 / recipientProjects.length : 0;
    return {
      rows: recipientProjects.map((project) => ({
        project,
        base: null,
        share,
        amount: Math.round(operationsTotal * share),
      })),
      totalShare: 1,
      error: null,
    };
  }, [recipientProjects, operationsTotal, base]);

  const showToast = (title, text) => setToast({ id: Date.now(), title, text });

  const loadHistory = useCallback(async () => {
    try {
      const runs = await getAllocations({ limit: 50 });
      const records = await Promise.all((Array.isArray(runs) ? runs : []).map(mapAllocationRun));
      setHistory(records);
      setNotice(null);
    } catch (error) {
      setNotice(error.message || "Не удалось загрузить историю распределений");
    }
  }, []);

  useEffect(() => {
    loadHistory();
    getDictionaryItems("projects", { limit: 500 })
      .then((items) => {
        setRecipientCatalog(
          (Array.isArray(items) ? items : []).map((item) => ({
            id: item.name,
            code: item.code || item.name,
            name: item.name,
            role: "recipient",
            department: "",
            projectType: "",
            buildingType: "",
            customer: "",
            startDate: null,
            endDate: null,
          }))
        );
      })
      .catch((error) => setNotice(error.message || "Не удалось загрузить проекты"));
  }, [loadHistory]);

  const loadWizard = ({ operationsMap, projectIds, nextBase, nextStep, restartOf }) => {
    setSelectedOperations(operationsMap);
    setSelectedProjects(projectIds);
    setBase(nextBase);
    setCreatedRecord(null);
    setRestartRecord(restartOf);
    setStep(nextStep);
    setWizardKey((key) => key + 1);
  };

  const startNewAllocation = () =>
    loadWizard({
      operationsMap: new Map(),
      projectIds: new Set(),
      nextBase: EMPTY_BASE,
      nextStep: 0,
      restartOf: null,
    });

  /** Мастер заполняется параметрами записи и открывается на проверке — их можно сразу подтвердить или изменить */
  const startRestart = async (record) => {
    let operationRows = record.params.operations;
    if (!operationRows.length && record.params.entryIds?.length) {
      try {
        const entries = await Promise.all(record.params.entryIds.map((id) => getJournalEntry(id)));
        operationRows = entries.map((entry) => mapJournalEntry(entry, "management"));
      } catch (error) {
        showToast("Не удалось открыть перезапуск", error.message);
        return;
      }
    }
    loadWizard({
      operationsMap: new Map(operationRows.map((operation) => [operation.id, operation])),
      projectIds: new Set(record.params.projectIds),
      nextBase: record.params.base,
      nextStep: REVIEW_STEP,
      restartOf: record,
    });
    setActiveTab("new");
  };

  /** Если в мастере есть несохранённые изменения, перезапуск сначала спрашивает подтверждение */
  const requestRestart = (record) => {
    if (restartRecord?.id === record.id && step !== RESULT_STEP) {
      setActiveTab("new");
      return;
    }
    const hasUnsavedWizard =
      step !== RESULT_STEP &&
      (Boolean(restartRecord) ||
        selectedOperations.size > 0 ||
        selectedProjects.size > 0 ||
        base !== EMPTY_BASE);
    if (hasUnsavedWizard) setPendingRestart(record);
    else startRestart(record);
  };

  const createAllocation = async () => {
    const dates = operations
      .map((operation) => {
        const [day, month, year] = String(operation.date || "").split(".");
        if (!year) return null;
        return new Date(Number(year), Number(month) - 1, Number(day));
      })
      .filter((date) => date && !Number.isNaN(date.getTime()));
    const from = dates.length ? new Date(Math.min(...dates.map((date) => date.getTime()))) : new Date();
    const to = dates.length ? new Date(Math.max(...dates.map((date) => date.getTime()))) : from;
    const method = base.method === "manual" ? "custom" : base.method;
    const percents = {};
    if (method === "custom") {
      recipientProjects.forEach((project) => {
        percents[project.name] = parsePercent(base.manualShares[project.id]);
      });
    }
    const payload = {
      date_from: format(from, "yyyy-MM-dd"),
      date_to: format(to, "yyyy-MM-dd"),
      source: { entry_ids: operations.map((operation) => String(operation.id)) },
      recipients: recipientProjects.map((project) => project.name),
      base: {
        type: method,
        items: base.articles?.[base.method] || [],
        percents,
      },
    };

    try {
      const preview = await previewAllocation(payload);
      const run = restartRecord
        ? await rerunAllocation(restartRecord.id, payload)
        : await createAllocationRequest(payload);
      const previewRows = (preview?.weights || []).map((weight) => {
        const share = Number(weight.share);
        const fraction = share > 1 ? share / 100 : share;
        const project =
          recipientProjects.find((item) => item.name === weight.project) || {
            id: weight.project,
            code: "",
            name: weight.project,
          };
        return {
          project,
          share: fraction,
          amount: Math.round(Number(preview.total_amount || operationsTotal) * fraction),
        };
      });
      const appliedAllocation = previewRows.length
        ? { rows: previewRows, totalShare: 1, error: null }
        : allocation;
      const now = new Date();
      const record = buildAllocationRecord({
        id: run.id,
        date: format(now, "dd.MM.yyyy"),
        time: format(now, "HH:mm"),
        author: authorName,
        operations,
        allocation: appliedAllocation,
        base,
        restarted: Boolean(restartRecord),
      });
      record.id = run.id;
      record.document = run.code || record.document;
      setRestartRecord(null);
      setCreatedRecord(record);
      setStep(RESULT_STEP);
      showToast(
        `Распределение ${record.document} проведено`,
        `Создано проводок: ${record.entries.length}`
      );
      loadHistory();
    } catch (error) {
      showToast("Не удалось провести распределение", error.message);
    }
  };

  const deleteRecord = async (record) => {
    try {
      await deleteAllocationRequest(record.id);
      setHistory((prev) => prev.filter((item) => item.id !== record.id));
      if (restartRecord?.id === record.id) startNewAllocation();
      showToast(
        `Распределение ${record.document} удалено`,
        `Удалено проводок: ${record.entries.length}. ${ONLY_THIS_RECORD_NOTE}`
      );
    } catch (error) {
      showToast("Не удалось удалить распределение", error.message);
    }
  };

  return (
    <div className="al">
      {notice && <div className="al-restart-banner">{notice}</div>}
      <div className="al-card">
        <div className="al-tabs" role="tablist">
          {ALLOCATION_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`al-tabs__tab${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "new" && (
          <Stepper
            steps={ALLOCATION_STEPS}
            current={step}
            locked={step === RESULT_STEP}
            onChange={setStep}
          />
        )}
      </div>

      {activeTab === "history" && (
        <HistoryTab history={history} onRestart={requestRestart} onDelete={deleteRecord} />
      )}

      <div hidden={activeTab !== "new"} key={wizardKey}>
        {restartRecord && (
          <div className="al-restart-banner">
            <div>
              <strong>Перезапуск распределения {restartRecord.document}</strong> от{" "}
              {restartRecord.date} · {restartRecord.period}. Параметры можно оставить или
              изменить на любом шаге. Проводки этого запуска будут удалены и созданы заново.{" "}
              {ONLY_THIS_RECORD_NOTE}
            </div>
            <button type="button" className="al-link" onClick={startNewAllocation}>
              Отменить перезапуск
            </button>
          </div>
        )}

        <div hidden={step !== 0}>
          <OperationsStep
            selected={selectedOperations}
            onSelectedChange={setSelectedOperations}
            onContinue={() => setStep(1)}
          />
        </div>
        <div hidden={step !== 1}>
          <RecipientsStep
            projects={recipientCatalog}
            operationsCount={operations.length}
            operationsTotalLabel={formatAmount(operationsTotal)}
            selected={selectedProjects}
            onSelectedChange={setSelectedProjects}
            onBack={() => setStep(0)}
            onContinue={() => setStep(2)}
          />
        </div>
        {step === 2 && (
          <BaseStep
            projects={recipientProjects}
            totalAmount={operationsTotal}
            operationsCount={operations.length}
            value={base}
            onChange={setBase}
            onBack={() => setStep(1)}
            onContinue={() => setStep(REVIEW_STEP)}
          />
        )}
        {step === REVIEW_STEP && (
          <ReviewStep
            operations={operations}
            totalAmount={operationsTotal}
            base={base}
            allocation={allocation}
            restartOf={restartRecord}
            onBack={() => setStep(2)}
            onConfirm={createAllocation}
          />
        )}
        {step === RESULT_STEP && createdRecord && (
          <ResultStep
            record={createdRecord}
            onNewAllocation={startNewAllocation}
            onOpenHistory={() => setActiveTab("history")}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(pendingRestart)}
        title={`Перезапустить ${pendingRestart?.document || ""}?`}
        text={
          restartRecord
            ? `Перезапуск ${restartRecord.document} ещё не подтверждён — его изменения будут потеряны.`
            : "В мастере есть незавершённое распределение — выбранные операции, проекты и база будут сброшены."
        }
        confirmLabel="Перезапустить"
        onCancel={cancelPendingRestart}
        onConfirm={() => {
          const record = pendingRestart;
          setPendingRestart(null);
          startRestart(record);
        }}
      />
      <Toast toast={toast} onClose={closeToast} />
    </div>
  );
}

export default Allocation;
